import { memo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { CalendarDaysIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { useBookings } from '../../hooks/useBookings'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { bookingAPI } from '../../services/api/bookings'
import { getInputClasses } from '../../styles/inputStyles'
import BookingStatusBadge from './BookingStatusBadge'
import BookingReviewPrompt from './BookingReviewPrompt'
import LoadingSpinner from '../LoadingSpinner'
import { toast } from '../../services/toastService'

/**
 * BookingsList - Customer's booking history.
 * Follows FavoritesSection list item conventions: expandable rows,
 * inline cancel form, per-item expanded detail view.
 * @idempotent @atomic
 */
const BookingsList = memo(function BookingsList() {
  const { t } = useTranslation()
  const { bookings, loading, error, cancelBooking, refresh } = useBookings()
  const { execute } = useAsyncOperation()

  const [expandedId, setExpandedId] = useState(null)
  const [cancellingId, setCancellingId] = useState(null)
  const [cancelReason, setCancelReason] = useState('')

  const toggleExpand = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }, [])

  const openCancel = useCallback((id) => {
    setCancellingId(id)
    setCancelReason('')
  }, [])

  const submitCancel = useCallback(async (groupId) => {
    await cancelBooking(groupId, cancelReason.trim() || undefined, {
      onSuccess: () => {
        toast.success(t('bookings.cancelled', 'Booking cancelled'))
        setCancellingId(null)
        setCancelReason('')
      },
      onError: () => toast.error(t('bookings.cancelFailed', 'Failed to cancel')),
    })
  }, [cancelBooking, cancelReason, t])

  if (loading) return <LoadingSpinner size="sm" />
  if (error) return <p className="text-sm text-red-500 py-3 text-center">{t('common.error', 'Failed to load')}</p>
  if (!bookings.length) return (
    <div className="p-3 text-center text-xs text-gray-500">{t('bookings.empty', 'No bookings yet')}</div>
  )

  return (
    <div className="space-y-2 p-2">
      {bookings.map((b) => {
        const isExpanded = expandedId === b.group_id
        const isActive = b.status !== 'completed' && b.status !== 'cancelled'
        const isCancelling = cancellingId === b.group_id
        const isCompleted = b.status === 'completed'

        return (
          <div
            key={b.group_id}
            className="bg-white rounded-md border border-gray-200/60 hover:border-gray-300 transition-all"
          >
            {/* Row header — tap to expand */}
            <button
              className="w-full flex items-start gap-2.5 p-3 text-left group"
              onClick={() => toggleExpand(b.group_id)}
              onTouchEnd={(e) => { e.preventDefault(); toggleExpand(b.group_id) }}
            >
              <CalendarDaysIcon className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 break-words group-hover:text-primary transition-colors">
                  {b.entity_name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {b.booking_count} {b.booking_count === 1
                    ? t('bookings.service', 'service')
                    : t('bookings.services', 'services')}
                  {' · '}
                  {new Date(b.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <BookingStatusBadge status={b.status} />
                {isExpanded
                  ? <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                  : <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
                }
              </div>
            </button>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-3 pb-3 pt-2 space-y-2">
                {/* Entity image if available */}
                {b.entity_image && (
                  <img
                    src={b.entity_image}
                    alt={b.entity_name}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                )}

                {/* Status info row */}
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span>{t('bookings.status.' + b.status, b.status)}</span>
                </div>

                {/* Cancel form */}
                {isActive && isCancelling && (
                  <div className="space-y-2 bg-gray-50 rounded-lg p-2">
                    <p className="text-xs font-medium text-gray-700">
                      {t('bookings.cancelConfirm', 'Cancel this booking?')}
                    </p>
                    <input
                      type="text"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder={t('bookings.notesPlaceholder', 'Reason (optional)')}
                      className={getInputClasses(false)}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => submitCancel(b.group_id)}
                        onTouchEnd={(e) => { e.preventDefault(); submitCancel(b.group_id) }}
                        className="flex-1 py-1.5 px-3 bg-red-500 text-white text-xs font-semibold rounded-lg hover:bg-red-600 transition-colors"
                      >
                        {t('bookings.cancel', 'Cancel booking')}
                      </button>
                      <button
                        onClick={() => setCancellingId(null)}
                        onTouchEnd={(e) => { e.preventDefault(); setCancellingId(null) }}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 rounded-lg border border-gray-200 transition-colors"
                      >
                        {t('common.back', 'Back')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Actions */}
                {isActive && !isCancelling && (
                  <button
                    onClick={() => openCancel(b.group_id)}
                    onTouchEnd={(e) => { e.preventDefault(); openCancel(b.group_id) }}
                    className="w-full py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                  >
                    {t('bookings.cancel', 'Cancel booking')}
                  </button>
                )}

                {/* Review prompt for completed bookings */}
                {isCompleted && (
                  <BookingReviewPrompt booking={b} entityId={b.entity_id} />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
})

export default BookingsList
